import {_} from 'lodash';
import {ObjectTypeId} from '../../../constants';
import {REMOVING_MESSAGE, RESTORING_MESSAGE, UPDATING_MESSAGE} from '../../../constants/Language';
import {IMessageData, IObjectData, IOverride} from '../../../index';
import {global, lm} from '../../../appContext';
import {createErrorMessage, getObjectDisplayName, getRawSyncFieldValue} from '../../../util/misc';
import PromiseRequest from '../../../req/PromiseRequest';
import {nsOverride} from '../../override/nsOverride';
import {nsFa} from '../../fa/fn';
import getBasicOverride = nsOverride.getBasicOverride;
import OverrideValue = nsOverride.OverrideValue;
import getOverrideStatusPillVariant = nsOverride.getOverrideStatusPillVariant;
import getOverrideStatusSuffix = nsOverride.getOverrideStatusSuffix;
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import getCancelPendingDeletionConfirmKind = nsOverride.getCancelPendingDeletionConfirmKind;
import requestRestoreOverride = nsOverride.requestRestoreOverride;
import TOverrideArPillVariant = nsOverride.TOverrideArPillVariant;
import i = nsFa.i;

export namespace nsGroupMemberOverride {

    /** Identum badge tone + fill (Figma node 1:1435) — shared A/R chrome from nsOverride. */
    export type MembershipOverridePillVariant = TOverrideArPillVariant;

    /** Plain row-action intents for group membership rows and overrides. */
    export type MembershipMemberActionKind = 'cancel' | 'restore' | 'remove' | 'add';

    /** Result messaging when `deleteoverride` runs for a membership override action. */
    export type MembershipOverrideActionOutcome = 'restore' | 'remove' | 'cancel';

    export interface IMembershipOverrideState {
        pillVariant: MembershipOverridePillVariant;
        actionKind: MembershipMemberActionKind;
        outcome: MembershipOverrideActionOutcome;
    }

    /** Action / outcome only — pill chrome comes from {@link getOverrideStatusPillVariant}. */
    interface IMembershipOverrideActions {
        actionKind: MembershipMemberActionKind;
        outcome: MembershipOverrideActionOutcome;
    }

    export interface IMembershipOverrideDisplay {
        translationKey: string;
        pillVariant: MembershipOverridePillVariant;
    }

    export interface IMembershipOverrideAction {
        actionKind: MembershipMemberActionKind;
        outcome: MembershipOverrideActionOutcome;
    }

    export interface IMembershipOverrideDeleteFeedback {
        pageMessage: string;
        snackbarMessage: string;
    }

    export interface IMembershipOverrideMutationCallbacks {
        onStart: (pageMessage: string) => void;
        onSuccess: () => void;
        onError: (message: IMessageData) => void;
        onFinally: () => void;
    }

    export interface IMembershipAddMemberFailure {
        user: IObjectData;
        error: string;
    }

    export interface IMembershipAddMembersBatchCallbacks {
        onProgress: (processed: number, total: number) => void;
        onComplete: (successes: IObjectData[], failures: IMembershipAddMemberFailure[]) => void;
    }

    const MEMBERSHIP_OVERRIDE_ALREADY_EXISTS = 'An override already exists for this object target mapping';

    /** `createoverride` response when the user already has a membership override on this group. */
    export function isMembershipAddMemberAlreadyExistsError(message: string): boolean {
        return String(message).includes(MEMBERSHIP_OVERRIDE_ALREADY_EXISTS);
    }

    const DEFAULT_ACTIONS: IMembershipOverrideActions = {
        actionKind: 'add',
        outcome: 'restore',
    };

    const MEMBERSHIP_OVERRIDE_ACTIONS: Record<string, IMembershipOverrideActions> = {
        'remove.pending': {
            actionKind: 'cancel',
            outcome: 'restore',
        },
        'remove.synced': {
            actionKind: 'restore',
            outcome: 'restore',
        },
        'add.pending': {
            actionKind: 'cancel',
            outcome: 'restore',
        },
        'add.synced': {
            actionKind: 'remove',
            outcome: 'remove',
        },
        'add.delete.pending': {
            actionKind: 'cancel',
            outcome: 'restore',
        },
        'remove.delete.pending': {
            actionKind: 'cancel',
            outcome: 'cancel',
        },
        'add.delete.synced': {
            actionKind: 'restore',
            outcome: 'restore',
        },
        'remove.delete.synced': {
            actionKind: 'remove',
            outcome: 'remove',
        },
    };

    function getMembershipOverrideState(override: IOverride): IMembershipOverrideState {
        const actions = MEMBERSHIP_OVERRIDE_ACTIONS[getOverrideStatusSuffix(override)] ?? DEFAULT_ACTIONS;
        return {
            pillVariant: getOverrideStatusPillVariant(override),
            actionKind: actions.actionKind,
            outcome: actions.outcome,
        };
    }

    /**
     * True when the override row is already soft-deleted (`deletedbyid` / `deletedwhen` set).
     * Suffix becomes `*.delete.*` (e.g. `add.delete.pending`).
     */
    export function isMembershipOverrideDeleted(override: IOverride): boolean {
        return !!override?.deletedbyid;
    }

    /**
     * Whether the UI may call `deleteoverride` on this override (not soft-deleted).
     * Soft-deleted (`*.delete.pending`) uses `restoreoverride` instead — see
     * {@link canShowMembershipOverrideAction} / {@link runMembershipOverrideRestore}.
     */
    export function canMutateMembershipOverride(override: IOverride): boolean {
        return !!override && !isMembershipOverrideDeleted(override);
    }

    /** Ellipsis may show for active mutates or cancel of pending soft-delete. */
    export function canShowMembershipOverrideAction(override: IOverride): boolean {
        return canMutateMembershipOverride(override) || isOverridePendingDeletion(override);
    }

    /** Confirm dialog before `restoreoverride` on `*.delete.pending`. */
    export function showCancelPendingDeletionConfirm(
        override: IOverride,
        onConfirm: () => void,
    ): void {
        const kind = getCancelPendingDeletionConfirmKind(override);
        if (!kind) {
            return;
        }
        const confirmKey = `override.membership.confirm.cancel.pending.${kind}`;
        const confirmLabel = kind === 'delete'
            ? 'override.membership.btn.keep.membership'
            : 'override.btn.keep.override';
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((${confirmKey}.title))`,
            content: `<p>${lm.t(confirmKey)}</p>`,
            buttons: {
                confirm: {
                    label: confirmLabel,
                    onClick: onConfirm,
                },
                cancel: {
                    label: 'cancel',
                },
            },
        });
    }

    /**
     * Action-specific confirm before mutating an existing membership override via `deleteoverride`
     * (cancel pending add/remove, remove synced add, restore synced remove).
     */
    export function showMembershipOverrideActionConfirm(
        override: IOverride,
        onConfirm: () => void,
    ): void {
        const {actionKind} = getMembershipOverrideAction(override);
        if (actionKind === 'add') {
            onConfirm();
            return;
        }
        const confirmKey = `override.membership.confirm.${actionKind}`;
        const confirmLabel = actionKind === 'cancel'
            ? 'override.membership.btn.cancel.change'
            : actionKind;
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((${confirmKey}.title))`,
            content: `<p>${lm.t(confirmKey)}</p>`,
            buttons: {
                confirm: {
                    label: confirmLabel,
                    onClick: onConfirm,
                },
                cancel: {
                    label: 'cancel',
                },
            },
        });
    }

    /** Overrides whose target is a user (membership subject). */
    export function filterUserMembershipOverrides(overrides: IOverride[]): IOverride[] {
        return (overrides ?? []).filter(o => o.targetobjecttypeid === ObjectTypeId.User);
    }

    /** Members grid status-filter discriminant (toolbar dropdown). */
    export type TGroupMembersTab = 'current' | 'pending' | 'added' | 'removed';

    /** Override not yet applied by sync (any `*.pending` suffix). */
    export function isMembershipOverridePending(override: IOverride): boolean {
        return !!override && !override.syncronizedimportfileid;
    }

    /** Synced remove override only (`remove.synced`). */
    export function isMembershipOverrideRemoved(override: IOverride): boolean {
        return getOverrideStatusSuffix(override) === 'remove.synced';
    }

    /** Synced add override only (`add.synced` — value `A` and import file synced). */
    export function isMembershipOverrideAdded(override: IOverride): boolean {
        return getOverrideStatusSuffix(override) === 'add.synced';
    }

    /** Overrides that define membership for a non-current members tab. */
    export function filterOverridesForMembersTab(
        overrides: IOverride[],
        tab: TGroupMembersTab,
    ): IOverride[] {
        const userOverrides = filterUserMembershipOverrides(overrides);
        switch (tab) {
            case 'current':
                return userOverrides;
            case 'pending':
                return userOverrides.filter(isMembershipOverridePending);
            case 'added':
                return userOverrides.filter(isMembershipOverrideAdded);
            case 'removed':
                return userOverrides.filter(isMembershipOverrideRemoved);
        }
    }

    /** Whether a loaded user row belongs on the selected members tab. */
    export function userMatchesMembersTab(
        overrides: IOverride[],
        user: IObjectData,
        tab: TGroupMembersTab,
    ): boolean {
        if (tab === 'current') {
            return true;
        }
        const override = findOverrideForUser(overrides, user);
        if (!override) {
            return false;
        }
        switch (tab) {
            case 'pending':
                return isMembershipOverridePending(override);
            case 'added':
                return isMembershipOverrideAdded(override);
            case 'removed':
                return isMembershipOverrideRemoved(override);
        }
    }

    /** Match a group membership override to a user row in the members list. */
    export function findOverrideForUser(overrides: IOverride[], user: IObjectData): IOverride | null {
        if (!overrides?.length) {
            return null;
        }
        const userSourceId = getRawSyncFieldValue('SourceId', user.syncfields);
        return overrides.find(o =>
            o.targetobjectid === user.id
            || (!!userSourceId && o.targetobjectsrc === userSourceId)
        ) ?? null;
    }

    /** `override.membership.*` translation key and pill styling for a membership override cell. */
    export function getMembershipOverrideDisplay(override: IOverride): IMembershipOverrideDisplay {
        return {
            translationKey: `override.membership.${getOverrideStatusSuffix(override)}`,
            pillVariant: getOverrideStatusPillVariant(override),
        };
    }

    /** Row-action kind and mutation outcome for a membership override. */
    export function getMembershipOverrideAction(override: IOverride): IMembershipOverrideAction {
        const {actionKind, outcome} = getMembershipOverrideState(override);
        return {actionKind, outcome};
    }

    /**
     * Busy + snackbar copy keyed by row **action** (cancel / remove / restore), not API outcome.
     * Soft-delete cancel uses {@link getMembershipCancelPendingDeletionFeedback}.
     */
    export function getMembershipOverrideActionFeedback(
        actionKind: MembershipMemberActionKind,
    ): IMembershipOverrideDeleteFeedback {
        switch (actionKind) {
            case 'restore':
                return {
                    pageMessage: RESTORING_MESSAGE,
                    snackbarMessage: 'override.membership.snackbar.restore',
                };
            case 'remove':
                return {
                    pageMessage: REMOVING_MESSAGE,
                    snackbarMessage: 'override.membership.snackbar.remove',
                };
            case 'cancel':
            case 'add':
                return {
                    pageMessage: UPDATING_MESSAGE,
                    snackbarMessage: 'override.membership.snackbar.cancel',
                };
        }
    }

    /** Busy + snackbar when cancelling a pending soft-delete via `restoreoverride`. */
    export function getMembershipCancelPendingDeletionFeedback(
        override: IOverride,
    ): IMembershipOverrideDeleteFeedback {
        const kind = getCancelPendingDeletionConfirmKind(override);
        return {
            pageMessage: UPDATING_MESSAGE,
            snackbarMessage: kind
                ? `override.membership.snackbar.cancel.pending.${kind}`
                : 'override.membership.snackbar.cancel',
        };
    }

    export function invalidateMembershipOverrideCaches(): void {
        global.cacheManager.removeCache('searchobject');
        global.cacheManager.removeCache('getobject');
    }

    /** `deleteoverride` with shared cache, snackbar, and feedback for a membership override action. */
    export function runMembershipOverrideDelete(
        req: PromiseRequest,
        override: IOverride,
        callbacks: IMembershipOverrideMutationCallbacks,
    ): void {
        // `deleteoverride` is a no-op / error server-side when `deletedbyid` is already set.
        if (!canMutateMembershipOverride(override)) {
            return;
        }
        const {actionKind} = getMembershipOverrideAction(override);
        const {pageMessage, snackbarMessage} = getMembershipOverrideActionFeedback(actionKind);
        callbacks.onStart(pageMessage);
        req.request({id: override.id})
            .then(() => {
                global.snackbarData({message: snackbarMessage});
                callbacks.onSuccess();
            })
            .catch((reason: string) => callbacks.onError(createErrorMessage(String(reason))))
            .finally(() => callbacks.onFinally());
    }

    /** `restoreoverride` for `*.delete.pending` — cancel soft-delete before sync. */
    export function runMembershipOverrideRestore(
        req: PromiseRequest,
        override: IOverride,
        callbacks: IMembershipOverrideMutationCallbacks,
    ): void {
        if (!isOverridePendingDeletion(override)) {
            return;
        }
        const {pageMessage, snackbarMessage} = getMembershipCancelPendingDeletionFeedback(override);
        callbacks.onStart(pageMessage);
        requestRestoreOverride(req, override)
            .then(() => {
                global.snackbarData({message: snackbarMessage});
                callbacks.onSuccess();
            })
            .catch((reason: string) => callbacks.onError(createErrorMessage(String(reason))))
            .finally(() => callbacks.onFinally());
    }

    /** `createoverride` with remove value when marking a synced member for removal. */
    export function runMembershipRemoveMember(
        req: PromiseRequest,
        group: IObjectData,
        user: IObjectData,
        callbacks: IMembershipOverrideMutationCallbacks,
    ): void {
        const override = buildRemoveOverrideForMember(group, user);
        const {pageMessage, snackbarMessage} = getMembershipOverrideActionFeedback('remove');
        callbacks.onStart(pageMessage);
        req.request({joverride: JSON.stringify(override)})
            .then(() => {
                global.snackbarData({message: snackbarMessage});
                callbacks.onSuccess();
            })
            .catch((reason: string) => callbacks.onError(createErrorMessage(String(reason))))
            .finally(() => callbacks.onFinally());
    }

    /** Payload for `createoverride` when removing a synced group member. */
    export function buildRemoveOverrideForMember(group: IObjectData, user: IObjectData): IOverride {
        // `targetobjectsrc` ← user syncfield `SourceId` (loaded via member searchobject tuples).
        return _.defaultsDeep({
            objectid: group.id,
            objectsrc: getRawSyncFieldValue('SourceId', group.syncfields),
            objecttypeid: ObjectTypeId.Group,
            sourcename: getObjectDisplayName(group),
            targetname: getObjectDisplayName(user),
            targetobjectid: user.id,
            targetobjectsrc: getRawSyncFieldValue('SourceId', user.syncfields),
            targetobjecttypeid: user.type?.id ?? ObjectTypeId.User,
            value: OverrideValue.Remove,
        } as IOverride, getBasicOverride());
    }

    /** Payload for `createoverride` when manually adding a user to a group. */
    export function buildAddOverrideForMember(group: IObjectData, user: IObjectData): IOverride {
        return _.defaultsDeep({
            objectid: group.id,
            objectsrc: getRawSyncFieldValue('SourceId', group.syncfields),
            objecttypeid: ObjectTypeId.Group,
            sourcename: getObjectDisplayName(group),
            targetname: getObjectDisplayName(user),
            targetobjectid: user.id,
            targetobjectsrc: getRawSyncFieldValue('SourceId', user.syncfields),
            targetobjecttypeid: user.type?.id ?? ObjectTypeId.User,
            value: OverrideValue.Add,
        } as IOverride, getBasicOverride());
    }

    /** Sequential `createoverride` per user; continues after individual failures and reports both outcomes. */
    export function runMembershipAddMembersBatch(
        req: PromiseRequest,
        group: IObjectData,
        users: IObjectData[],
        callbacks: IMembershipAddMembersBatchCallbacks,
    ): void {
        if (!users.length) {
            callbacks.onComplete([], []);
            return;
        }
        const total = users.length;
        const successes: IObjectData[] = [];
        const failures: IMembershipAddMemberFailure[] = [];
        let index = 0;

        const processNext = (): Promise<void> => {
            if (index >= users.length) {
                return Promise.resolve();
            }
            const user = users[index++];
            if (!user) {
                return processNext();
            }
            return req.request({
                joverride: JSON.stringify(buildAddOverrideForMember(group, user)),
            })
                .then(() => {
                    successes.push(user);
                    callbacks.onProgress(successes.length + failures.length, total);
                    return processNext();
                })
                .catch((reason: string) => {
                    failures.push({user, error: reason});
                    callbacks.onProgress(successes.length + failures.length, total);
                    return processNext();
                });
        };

        void processNext().then(() => {
            invalidateMembershipOverrideCaches();
            callbacks.onComplete(successes, failures);
        });
    }

}
